import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  projectApi,
  type Project,
  type CreateProjectData,
  type UpdateProjectData,
} from '../api/projectApi';
import { billingStatusKey } from './useBilling';

// Query keys for React Query cache management
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: string) => [...projectKeys.lists(), { filters }] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  byUser: (userId: string) => [...projectKeys.all, 'user', userId] as const,
  byClient: (clientId: string) => [...projectKeys.all, 'client', clientId] as const,
};

function getProjectClientId(client: Project['client']): string | null {
  if (!client) return null;
  return typeof client === 'string' ? client : client._id;
}

function invalidateClientProjects(
  queryClient: ReturnType<typeof useQueryClient>,
  client: Project['client']
) {
  const clientId = getProjectClientId(client);
  if (clientId) {
    queryClient.invalidateQueries({ queryKey: projectKeys.byClient(clientId) });
  }
}

// Hook to add a file to a project spec entry
export const useAddSpecFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, specIdx, url, fileDescription }: { projectId: string; specIdx: number; url: string; fileDescription?: string }) =>
      projectApi.addSpecFile(projectId, specIdx, url, fileDescription),
    onSuccess: (updatedProject) => {
      // Invalidate and refetch the project detail and lists
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(updatedProject._id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
};

// Hook to remove a file from a project spec entry
export const useRemoveSpecFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, specIdx, fileIdx }: { projectId: string; specIdx: number; fileIdx: number }) =>
      projectApi.removeSpecFile(projectId, specIdx, fileIdx),
    onSuccess: (updatedProject: Project) => {
      // Invalidate and refetch the project detail and lists
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(updatedProject._id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
};

// Hook to fetch all projects, with showAll toggle
export const useProjects = (showAll: boolean = false, includeArchived: boolean = true) => {
  return useQuery({
    queryKey: [...projectKeys.lists(), { showAll, includeArchived }],
    queryFn: () => projectApi.getAllProjects(showAll, includeArchived),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to fetch a single project by ID
export const useProject = (id: string) => {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => projectApi.getProjectById(id),
    enabled: !!id, // Only run if id is provided
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to fetch projects by user
export const useProjectsByUser = (userId: string) => {
  return useQuery({
    queryKey: projectKeys.byUser(userId),
    queryFn: () => projectApi.getProjectsByUser(userId),
    enabled: !!userId, // Only run if userId is provided
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to fetch projects by client
export const useProjectsByClient = (clientId: string) => {
  return useQuery({
    queryKey: projectKeys.byClient(clientId),
    queryFn: () => projectApi.getProjectsByClient(clientId),
    enabled: !!clientId, // Only run if clientId is provided
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to create a new project
export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: projectApi.createProject,
    onSuccess: (newProject: Project) => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });

      // Invalidate projects by user and client
      queryClient.invalidateQueries({
        queryKey: projectKeys.byUser(newProject.userWhoCreatedProject._id),
      });

      invalidateClientProjects(queryClient, newProject.client);

      // Add the new project to the cache
      queryClient.setQueryData(projectKeys.detail(newProject._id), newProject);
    },
    onError: error => {
      console.error('Failed to create project:', error);
    },
  });
};

// Hook to update an existing project
export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectData }) =>
      projectApi.updateProject(id, data),
    onSuccess: (updatedProject: Project) => {
      // Update the specific project in cache
      queryClient.setQueryData(projectKeys.detail(updatedProject._id), updatedProject);

      // Invalidate related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: projectKeys.byUser(updatedProject.userWhoCreatedProject._id),
      });

      invalidateClientProjects(queryClient, updatedProject.client);
    },
    onError: error => {
      console.error('Failed to update project:', error);
    },
  });
};

// Hook to delete a project
export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: projectApi.deleteProject,
    onSuccess: (_, deletedId: string) => {
      // Remove the project from cache
      queryClient.removeQueries({ queryKey: projectKeys.detail(deletedId) });

      // Invalidate all project lists
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.invalidateQueries({ queryKey: billingStatusKey });
    },
    onError: error => {
      console.error('Failed to delete project:', error);
    },
  });
};

// Hook to add a task to a project
export const useAddTaskToProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, taskId }: { projectId: string; taskId: string }) =>
      projectApi.addTaskToProject(projectId, taskId),
    onSuccess: (updatedProject: Project) => {
      // Update the specific project in cache
      queryClient.setQueryData(projectKeys.detail(updatedProject._id), updatedProject);

      // Invalidate related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: projectKeys.byUser(updatedProject.userWhoCreatedProject._id),
      });

      invalidateClientProjects(queryClient, updatedProject.client);

      // You might also want to invalidate task queries if you have them
      // queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: error => {
      console.error('Failed to add task to project:', error);
    },
  });
};

// Hook to remove a task from a project
export const useRemoveTaskFromProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, taskId }: { projectId: string; taskId: string }) =>
      projectApi.removeTaskFromProject(projectId, taskId),
    onSuccess: (updatedProject: Project) => {
      // Update the specific project in cache
      queryClient.setQueryData(projectKeys.detail(updatedProject._id), updatedProject);

      // Invalidate related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: projectKeys.byUser(updatedProject.userWhoCreatedProject._id),
      });

      invalidateClientProjects(queryClient, updatedProject.client);
    },
    onError: error => {
      console.error('Failed to remove task from project:', error);
    },
  });
};

// Hook for optimistic updates when creating a project
export const useCreateProjectOptimistic = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: projectApi.createProject,
    onMutate: async (newProject: CreateProjectData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectKeys.lists() });

      // Snapshot the previous value
      const previousProjects = queryClient.getQueryData(projectKeys.lists());

      // Optimistically update to the new value
      const optimisticProject: Project = {
        _id: `temp-${Date.now()}`, // Temporary ID
        userWhoCreatedProject: {
          _id: newProject.userWhoCreatedProject,
          firstName: 'Loading',
          lastName: '...',
          email: 'loading@example.com',
        },
        projectNumber: 'PP-Loading...', // Temporary project number
        projectName: newProject.projectName,
        type: newProject.type,
        client: newProject.client,
        tasks: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueryData(projectKeys.lists(), (old: Project[] | undefined) =>
        old ? [...old, optimisticProject] : [optimisticProject]
      );

      // Return a context object with the snapshotted value
      return { previousProjects };
    },
    onError: (err, newProject, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(projectKeys.lists(), context?.previousProjects);
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
};

// Hook to upload project specification file
export const useUploadProjectSpec = () => {
  return useMutation({
    mutationFn: projectApi.uploadProjectSpec,
    onSuccess: (url: string) => {
      console.log('File uploaded successfully:', url);
    },
    onError: error => {
      console.error('Failed to upload project specification file:', error);
    },
  });
};

// Hook to archive/unarchive a project
export const useArchiveProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      projectApi.archiveProject(id, archived),
    onSuccess: (updatedProject: Project) => {
      // Invalidate and refetch projects list
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(updatedProject._id) });
      // Invalidate by user and client
      queryClient.invalidateQueries({
        queryKey: projectKeys.byUser(updatedProject.userWhoCreatedProject._id),
      });
      invalidateClientProjects(queryClient, updatedProject.client);
    },
  });
};
